import { useEffect, useState } from 'react';
import {
  Drawer,
  FormField,
  TextInput,
  Select,
  Textarea,
  DrawerForm,
  DrawerFormActions,
} from '../../components/index.js';

const DOCUMENT_TYPES = [
  { value: 'intakeForm', label: 'Intake form' },
  { value: 'businessCase', label: 'Business case' },
  { value: 'riskRegister', label: 'Risk register' },
  { value: 'designReview', label: 'Design review' },
  { value: 'testReport', label: 'Test report' },
  { value: 'financialAnalysis', label: 'Financial analysis' },
  { value: 'aiArtifact', label: 'AI artifact' },
  { value: 'other', label: 'Other' },
];

const CLASSIFICATION_LEVELS = [
  { value: 'Illustrative Data', label: 'Illustrative Data' },
  { value: 'Unclassified', label: 'Unclassified' },
  { value: 'CUI', label: 'CUI' },
  { value: 'Secret', label: 'Secret' },
  { value: 'Top Secret', label: 'Top Secret' },
];

const DEFAULTS = {
  title: '',
  documentType: 'financialAnalysis',
  classificationLabel: 'Illustrative Data',
  criterionId: '',
  summary: '',
  notes: '',
};

export function AttachEvidenceDrawer({ open, onClose, criteria = [], onSubmit, submitting }) {
  const [draft, setDraft] = useState(DEFAULTS);
  const [errors, setErrors] = useState({});

  // Reset when drawer reopens. Pre-select first criterion if available.
  useEffect(() => {
    if (open) {
      setDraft({
        ...DEFAULTS,
        criterionId: criteria[0]?.id || '',
      });
      setErrors({});
    }
  }, [open, criteria]);

  function update(field, value) {
    setDraft((d) => ({ ...d, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: null }));
  }

  function validate() {
    const next = {};
    if (!draft.title.trim()) next.title = 'Title is required.';
    if (!draft.criterionId) next.criterionId = 'Pick a criterion to link this evidence to.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    onSubmit?.({
      title: draft.title.trim(),
      documentType: draft.documentType,
      classificationLabel: draft.classificationLabel,
      criterionId: draft.criterionId,
      summary: draft.notes
        ? `${draft.summary}\n\nNotes: ${draft.notes}`.trim()
        : draft.summary.trim(),
    });
  }

  return (
    <Drawer open={open} onClose={onClose} title="Attach Evidence" width={520}>
      <DrawerForm onSubmit={handleSubmit} submitting={submitting}>
        <FormField
          label="Document title"
          htmlFor="attach-title"
          required
          error={errors.title}
          hint="Illustrative upload — no file binary is attached."
        >
          <TextInput
            id="attach-title"
            value={draft.title}
            onChange={(v) => update('title', v)}
            placeholder="e.g. Q3 Cost Variance Memo"
            autoFocus
          />
        </FormField>

        <FormField label="Document type" htmlFor="attach-doc-type">
          <Select
            id="attach-doc-type"
            value={draft.documentType}
            onChange={(v) => update('documentType', v)}
            options={DOCUMENT_TYPES}
          />
        </FormField>

        <FormField label="Classification" htmlFor="attach-classification">
          <Select
            id="attach-classification"
            value={draft.classificationLabel}
            onChange={(v) => update('classificationLabel', v)}
            options={CLASSIFICATION_LEVELS}
          />
        </FormField>

        <FormField
          label="Gate criterion"
          htmlFor="attach-criterion"
          required
          error={errors.criterionId}
        >
          <Select
            id="attach-criterion"
            value={draft.criterionId}
            onChange={(v) => update('criterionId', v)}
            options={criteria.map((c) => ({ value: c.id, label: c.title || c.name || c.id }))}
            placeholder={criteria.length ? 'Select criterion…' : 'No criteria on this gate'}
            disabled={!criteria.length}
          />
        </FormField>

        <FormField label="Summary" htmlFor="attach-summary">
          <Textarea
            id="attach-summary"
            value={draft.summary}
            onChange={(v) => update('summary', v)}
            placeholder="Short description of what this evidence covers."
            rows={3}
          />
        </FormField>

        <FormField label="Reviewer notes (optional)" htmlFor="attach-notes">
          <Textarea
            id="attach-notes"
            value={draft.notes}
            onChange={(v) => update('notes', v)}
            placeholder="Where it came from, who verified it, signed-off date…"
            rows={3}
          />
        </FormField>

        <DrawerFormActions
          onCancel={onClose}
          submitLabel="Attach evidence"
          submitting={submitting}
        />
      </DrawerForm>
    </Drawer>
  );
}
